import i18next from "src/i18n/config";
import { filesize } from "src/ui/utils/filesize";

import { HardwareIconColor } from "./battery";

type MemoryState = API.DeviceState["memory"] | null | undefined;

// used-memory percentage bands driving the usage icon color
const usedWarningThreshold = 75;
const usedCriticalThreshold = 90;

type MemoryUsageIcon = {
    icon: string;
    color: HardwareIconColor;
};

type MemoryAlert = {
    icon: string;
    color: HardwareIconColor;
    isLow: boolean;
};

// percentage of memory that is in use, or null when the total is unknown.
// this is the single source of truth for the used-vs-available math: the device
// reports `available`/`total`, and "used" is everything that is not available.
const getMemoryUsedPercentage = (memory: MemoryState): number | null => {
    const total = memory?.total ?? 0;
    if (total <= 0) {
        return null;
    }
    const available = memory?.available ?? 0;
    const used = total - available;
    return Math.round((used / total) * 100);
};

// human-readable "usedBytes / totalBytes (usedPercentage%)" for the usage rows
export const getMemoryUsageText = (memory: MemoryState): string => {
    const usedPercentage = getMemoryUsedPercentage(memory);
    if (usedPercentage === null) {
        return i18next.t("sidepanels:devicePanel.hardwareTab.notAvailable");
    }
    const total = memory!.total;
    const used = total - (memory?.available ?? 0);
    return `${filesize(used)} / ${filesize(total)} (${usedPercentage}%)`;
};

// icon + color for the amount of memory in use (independent of the `isLow` alert)
export const getMemoryUsageIcon = (memory: MemoryState): MemoryUsageIcon => {
    const usedPercentage = getMemoryUsedPercentage(memory);
    if (usedPercentage === null) {
        return { icon: "fa-microchip", color: HardwareIconColor.SECONDARY };
    }
    if (usedPercentage >= usedCriticalThreshold) {
        return { icon: "fa-microchip", color: HardwareIconColor.ERROR };
    }
    if (usedPercentage >= usedWarningThreshold) {
        return { icon: "fa-microchip", color: HardwareIconColor.WARNING };
    }
    return { icon: "fa-microchip", color: HardwareIconColor.SUCCESS };
};

// the device-reported low-memory alert, rendered as its own signal. This is the
// backend `isLow` flag and is deliberately separate from the usage math above:
// a device can flag low memory even while overall usage looks healthy. Only the
// icon/color are shared; the accompanying text is contextual per tab.
export const getMemoryAlert = (memory: MemoryState): MemoryAlert => {
    if (memory?.isLow) {
        return {
            icon: "fa-triangle-exclamation",
            color: HardwareIconColor.ERROR,
            isLow: true,
        };
    }
    return {
        icon: "fa-circle-check",
        color: HardwareIconColor.SECONDARY,
        isLow: false,
    };
};
